import { DividendFrequency, DividendTimelineRecord, Security } from '../types';
import { logTelemetry } from './telemetryService';
import { runWithMassiveRateLimit } from './massiveRateLimiter';

const getEnvValue = (key: string) =>
    import.meta.env?.[key] ??
    (typeof process !== 'undefined' ? process.env?.[key] : undefined);

const MASSIVE_ENDPOINT_BASE =
    (getEnvValue('VITE_MARKET_DATA_ENDPOINT_BASE_URL') as string | undefined) ||
    (getEnvValue('VITE_QUOTE_ENDPOINT_BASE_URL') as string | undefined);

const buildMassiveEndpoint = (path: string, params?: Record<string, string>) => {
    const base = MASSIVE_ENDPOINT_BASE?.replace(/\/$/, '') ?? '';
    if (!base) return null;
    const query = params ? new URLSearchParams(params).toString() : '';
    return `${base}/${path}${query ? `?${query}` : ''}`;
};

const providerIdCache = new Map<string, string>();
const dividendCache = new Map<string, DividendTimelineRecord[]>();

const normalizeUkTicker = (symbol: string, exchange?: string, currency?: string) => {
    if (symbol.toUpperCase().endsWith('.L')) return symbol.toUpperCase();
    const ex = exchange?.toUpperCase() || '';
    const curr = currency?.toUpperCase() || '';
    if (ex.includes('LSE') || ex.includes('XLON') || ex.includes('LON') || curr === 'GBP' || curr === 'GBX') {
        return `${symbol.toUpperCase()}.L`;
    }
    return symbol.toUpperCase();
};

const mapFrequency = (providerFrequency?: string | number): DividendFrequency => {
    if (typeof providerFrequency === 'number') {
        if (providerFrequency >= 11) return 'Monthly';
        if (providerFrequency >= 4) return 'Quarterly';
        if (providerFrequency === 2) return 'Bi-Annually';
        if (providerFrequency === 1) return 'Annually';
    }

    if (typeof providerFrequency === 'string') {
        const normalized = providerFrequency.toLowerCase();
        if (normalized.includes('month')) return 'Monthly';
        if (normalized.includes('quarter')) return 'Quarterly';
        if (normalized.includes('semi') || normalized.includes('bi')) return 'Bi-Annually';
        if (normalized.includes('annual') || normalized.includes('year')) return 'Annually';
    }

    return 'Irregular';
};

export const resolveProviderId = async (
    security: Security,
    opts?: { uid?: string | null }
): Promise<string | null> => {
    const sym = (security.symbol || '').trim().toUpperCase();
    const exg = (security.exchange || '').trim().toUpperCase();
    const isin = (security.isin || '').trim().toUpperCase();
    const dp = (security.dataProviderId || '').trim().toUpperCase();

    const cacheKey = `${sym}|${exg}|${isin}|${dp}`;
    const cached = providerIdCache.get(cacheKey);
    if (cached) return cached;

    const candidateSymbol = normalizeUkTicker(sym, exg, security.currency);
    const massiveEndpointBase = buildMassiveEndpoint('getMassiveReferenceTickers');
    if (!massiveEndpointBase) {
        console.warn(
            'Massive endpoint base is not configured. Set VITE_MARKET_DATA_ENDPOINT_BASE_URL or VITE_QUOTE_ENDPOINT_BASE_URL.'
        );
        providerIdCache.set(cacheKey, candidateSymbol);
        return candidateSymbol;
    }

    if (dp) {
        const looksSuspicious = dp.length <= 2 && sym.length >= 3 && dp !== sym;
        if (!looksSuspicious) {
            providerIdCache.set(cacheKey, dp);
            return dp;
        }
    }

    if (isin) {
        try {
            const url = buildMassiveEndpoint('getMassiveReferenceTickers', { isin, limit: '1' });
            if (!url) throw new Error('Massive endpoint base not configured');
            const response = await runWithMassiveRateLimit(() => fetch(url), { label: 'isin-lookup' });
            if (response.ok) {
                const data = await response.json();
                const results = Array.isArray(data?.results) ? data.results : [];
                const baseSym = candidateSymbol.replace(/\.L$/i, '');
                const exactMatch = results
                    .map((item: any) => (item?.ticker as string | undefined)?.toUpperCase())
                    .find((ticker) => ticker && ticker.replace(/\.L$/i, '') === baseSym);
                const match = exactMatch || (results[0]?.ticker as string | undefined)?.toUpperCase();
                if (match) {
                    const baseMatch = match.replace(/\.L$/i, '');
                    const mismatch = baseSym && baseMatch && baseSym !== baseMatch;
                    const likelyNoise = baseMatch.length <= 2 || baseSym.length <= 2;

                    if (!mismatch) {
                        providerIdCache.set(cacheKey, match);
                        return match;
                    }

                    if (!likelyNoise) {
                        console.warn('ISIN resolved ticker mismatch; ignoring ISIN result', {
                            sym,
                            candidateSymbol,
                            match,
                            isin,
                        });
                    }
                }
            } else {
                const body = await response.text().catch(() => '');
                await logTelemetry(opts?.uid, {
                    provider: 'MASSIVE',
                    category: 'ASSET_DETAIL',
                    level: response.status === 429 ? 'warn' : 'error',
                    symbol: sym || candidateSymbol,
                    requestPath: url,
                    status: response.status,
                    message: `Massive failed: ${response.status} ${response.statusText}`,
                    meta: {
                        isin,
                        exchange: exg,
                        responseSnippet: body.slice(0, 200),
                    },
                });
            }
        } catch (error) {
            try {
                await logTelemetry(opts?.uid, {
                    provider: 'MASSIVE',
                    category: 'ASSET_DETAIL',
                    level: 'error',
                    symbol: sym || candidateSymbol,
                    requestPath: 'getMassiveReferenceTickers',
                    message: 'Failed to resolve provider ID from ISIN',
                    meta: {
                        isin,
                        exchange: exg,
                        error: error instanceof Error ? error.message : String(error),
                    },
                });
            } catch (telemetryError) {
                console.warn('Telemetry logging failed', telemetryError);
            }
            console.error('Failed to resolve provider ID from ISIN', error);
        }
    }

    providerIdCache.set(cacheKey, candidateSymbol);
    return candidateSymbol;
};

export const fetchDividendTimeline = async (security: Security): Promise<DividendTimelineRecord[]> => {
    const massiveEndpointBase = buildMassiveEndpoint('getMassiveDividends');
    if (!massiveEndpointBase) {
        console.warn(
            'Massive endpoint base is not configured. Set VITE_MARKET_DATA_ENDPOINT_BASE_URL or VITE_QUOTE_ENDPOINT_BASE_URL.'
        );
        return [];
    }

    const providerId = await resolveProviderId(security);
    if (!providerId) return [];

    if (dividendCache.has(providerId)) {
        return dividendCache.get(providerId)!;
    }

    try {
        const url = buildMassiveEndpoint('getMassiveDividends', { ticker: providerId, limit: '120' });
        if (!url) throw new Error('Massive endpoint base not configured');
        const response = await runWithMassiveRateLimit(() => fetch(url), { label: 'dividend-timeline' });
        if (!response.ok) {
            console.error('Dividend API failed', response.status, response.statusText);
            return [];
        }

        const data = await response.json();
        const today = new Date();

        const dividends: DividendTimelineRecord[] = (data?.results || []).map((item: any) => {
            const payDate = item.pay_date || item.payment_date || item.payable_date;
            const exDate = item.ex_dividend_date || item.exDate;
            const amount = Number(item.cash_amount || item.amount || 0);
            const currency = item.currency || item.currency_code || security.currency || 'USD';

            return {
                exDate,
                payDate,
                amount,
                currency,
                isUpcoming: payDate ? new Date(payDate) > today : false,
                isSpecial: (item.dividend_type || item.type || '').toLowerCase().includes('special'),
                frequency: mapFrequency(item.frequency || item.pay_frequency || item.payout_frequency),
            };
        });

        dividendCache.set(providerId, dividends);
        return dividends;
    } catch (error) {
        console.error('Failed to fetch dividend timeline', error);
        return [];
    }
};
